import React, { useState, useEffect } from 'react';
import { emailTemplatesAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus,
  Mail,
  Pencil,
  Trash2,
  Loader2,
  FileText,
} from 'lucide-react';

const EmailTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState({
    name: '',
    subject: '',
    content: '',
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await emailTemplatesAPI.getAll();
      setTemplates(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des modèles');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ name: '', subject: '', content: '' });
    setEditingTemplate(null);
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setForm({
      name: template.name,
      subject: template.subject || '',
      content: template.content,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      if (editingTemplate) {
        await emailTemplatesAPI.update(editingTemplate.id, form);
        toast.success('Modèle mis à jour');
      } else {
        await emailTemplatesAPI.create(form);
        toast.success('Modèle créé');
      }
      fetchTemplates();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'opération');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce modèle ?')) return;

    try {
      await emailTemplatesAPI.delete(id);
      toast.success('Modèle supprimé');
      fetchTemplates();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            Modèles d'email
          </h1>
          <p className="text-slate-500 mt-1">{templates.length} modèle(s) enregistré(s)</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-yellow-500 hover:bg-yellow-600 text-slate-900" data-testid="add-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau modèle
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
                {editingTemplate ? 'Modifier le modèle' : 'Nouveau modèle d\'email'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="template-name">Nom du modèle *</Label>
                <Input
                  id="template-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Devis standard, Relance, Promotion..."
                  required
                  data-testid="template-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="template-subject">Sujet de l'email (optionnel)</Label>
                <Input
                  id="template-subject"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Laissez vide pour utiliser le sujet par défaut"
                  data-testid="template-subject-input"
                />
                <p className="text-xs text-slate-500">Si vide, le sujet sera "Devis N° [REF] - IDMAT OI"</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="template-content">Contenu du message *</Label>
                <Textarea
                  id="template-content"
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  placeholder="Bonjour,&#10;&#10;Veuillez trouver ci-joint notre devis pour votre projet...&#10;&#10;Cordialement,"
                  rows={6}
                  required
                  data-testid="template-content-input"
                />
                <p className="text-xs text-slate-500">Ce texte sera affiché dans le corps de l'email</p>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="bg-yellow-500 hover:bg-yellow-600 text-slate-900"
                  disabled={formLoading || !form.name || !form.content}
                  data-testid="save-template-btn"
                >
                  {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingTemplate ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Liste des modèles */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {templates.length === 0 ? (
            <div className="p-8 text-center">
              <Mail className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Aucun modèle d'email enregistré</p>
              <p className="text-sm text-slate-400 mt-1">
                Créez des modèles pour accélérer l'envoi de vos devis
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Nom
                    </TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Sujet
                    </TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Aperçu du contenu
                    </TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template.id} data-testid={`template-row-${template.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
                            <FileText className="w-4 h-4 text-yellow-600" />
                          </div>
                          <span className="font-medium text-slate-800">{template.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {template.subject || <span className="text-slate-400 italic">Par défaut</span>}
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-600 truncate max-w-xs">
                          {template.content.substring(0, 80)}...
                        </p>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(template)}
                            data-testid={`edit-template-${template.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(template.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-template-${template.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="border-slate-200 bg-slate-50">
        <CardContent className="p-4">
          <h3 className="font-medium text-slate-800 mb-2">💡 Utilisation</h3>
          <p className="text-sm text-slate-600">
            Ces modèles seront disponibles lors de l'envoi d'un devis par email. 
            Vous pourrez sélectionner un modèle pour pré-remplir le message personnalisé.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailTemplatesPage;
